import { QueryClient, useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query';

import { ENDPOINTS } from '@/api/endpoints';
import { fetchClient } from '@/api/fetch-client';
import {
  CarePlanNotesError,
  CarePlanNotesRequest,
  CarePlanNotesResponse,
  CreateCarePlanActivityNoteError,
  CreateCarePlanActivityNoteRequest,
  CreateCarePlanActivityNoteResponse,
} from '@/api/model';
import { removeEmptyParams } from '@/api/utils';

const KEY = {
  GET_CARE_PLAN_NOTES: 'GET_CARE_PLAN_NOTES',
  POST_CARE_PLAN_ACTIVITY_NOTE: 'POST_CARE_PLAN_ACTIVITY_NOTE',
} as const;

const request = {
  createCarePlanActivityNote: async ({
    payload: { activity_id: activityId, body },
  }: CreateCarePlanActivityNoteRequest) => {
    const { data } = await fetchClient.FCT.post<CreateCarePlanActivityNoteResponse>(
      `${ENDPOINTS.FCT.SALESFORCE.CARE_PLAN_ACTIVITIES}/${activityId}/notes`,
      { body },
    );

    return data;
  },
  getCarePlanNotes: async (
    { payload: { care_plan_id: carePlanId, includeEventLog, title } }: CarePlanNotesRequest,
    signal?: AbortSignal,
  ) => {
    const { data } = await fetchClient.FCT.get<CarePlanNotesResponse>(
      `${ENDPOINTS.FCT.SALESFORCE.CARE_PLANS}/${carePlanId}/notes`,
      {
        params: removeEmptyParams({ includeEventLog, title }),
        signal,
      },
    );

    return data;
  },
};

const query = {
  useGetCarePlanNotes: (
    { payload }: CarePlanNotesRequest,
    config?: Partial<UseQueryOptions<CarePlanNotesResponse, CarePlanNotesError>>,
  ) =>
    useQuery<CarePlanNotesResponse, CarePlanNotesError>({
      queryFn: ({ signal }) => request.getCarePlanNotes({ payload }, signal),
      queryKey: [
        KEY.GET_CARE_PLAN_NOTES,
        payload.care_plan_id,
        payload.includeEventLog,
        payload.title,
      ],
      ...config,
    }),
};

const mutation = {
  useCreateCarePlanActivityNote: (queryClient: QueryClient) =>
    useMutation<
      CreateCarePlanActivityNoteResponse,
      CreateCarePlanActivityNoteError,
      CreateCarePlanActivityNoteRequest
    >({
      mutationFn: request.createCarePlanActivityNote,
      onSuccess: (value) => {
        queryClient.invalidateQueries({
          queryKey: [KEY.GET_CARE_PLAN_NOTES],
        });

        return value;
      },
    }),
};

export const carePlanNotes = {
  KEY,
  mutation,
  query,
  request,
};
